<template>
  <v-app>
    <v-main>
      <v-row no-gutters>
        <v-col
          :cols="viewerCols"
        >
          <viewer
            :style="$vuetify.breakpoint.xsOnly ? 'height:60vh;' : 'height:100vh;'"
            @arClick="arAction()"
            @resetClick="resetAction()"
            @dimensionsClick="dimensionsAction()"
            @screenshotClick="screenshotAction()"
          />
        </v-col>
        <v-col
          v-if="isOptionsPanelDisplayed"
          :cols="optionsPanelCols"
          :style="optionsPanelDividersStyle"
        >
          <options-panel
            :style="$vuetify.breakpoint.xsOnly ? 'height:40vh;' : 'height:100vh;'"
            :options="options"
            @optionsItemDidSelected="loadOptionsItem"
          />
        </v-col>
      </v-row>

      <qrcode-ar
        v-model="isQrcodeArDisplayed"
        :url="qrcodeArUrl"
      />
    </v-main>
  </v-app>
</template>

<script>
  import scheme from './scheme/scheme.js'
  import transmit from './js/transmit.js'
  import helpers from './js/helpers.js'

  import { mapGetters, mapActions } from 'vuex'
  import Viewer from './components/viewer/Viewer.vue'
  import OptionsPanel from './components/options/OptionsPanel.vue'
  import QrcodeAr from './components/qrcode/QrcodeAr.vue'

  export default {
    name: 'App',

    components: {
      Viewer,
      QrcodeAr,
      OptionsPanel,
    },

    data () {
      return {
        // Conf web data
        options: null,
        imageOnHover: null,
        nameOnHover: null,

        // UI
        dividers: true,

        // AR
        isQrcodeArDisplayed: false,
        qrcodeArUrl: null,

        // Screenshot
        isFrontScreenshotEnabled: false,
      }
    },

    computed: {
      ...mapGetters(['isProductLoaded']),
      optionsPanelCols () {
        if (this.$vuetify.breakpoint.xsOnly) return '12'
        else if (this.$vuetify.breakpoint.smOnly) return '5'
        else if (this.$vuetify.breakpoint.mdOnly) return '4'
        return '3'
      },
      viewerCols () {
        if (!this.isOptionsPanelDisplayed || this.$vuetify.breakpoint.xsOnly) return '12'
        return 12 - this.optionsPanelCols
      },
      isOptionsPanelDisplayed () {
        return this.isProductLoaded && this.options != null && this.options.length > 0
      },
      optionsPanelDividersStyle () {
        if (!this.dividers) return ''
        else if (this.$vuetify.breakpoint.xsOnly) return 'border-top: solid lightgray 0.08rem;'
        else return 'border-left: solid lightgray 0.08rem;'
      },
    },

    created () {
      console.log(scheme.title + ' version: ' + require('../package.json').version)
      document.title = scheme.title
      window.addEventListener('message', this.listenMessages, false)
    },

    mounted () {
      this.loadViewer()
    },

    methods: {
      ...mapActions([
        'error',
        'setProductLoaded',
        'setProductNameEnabled',
        'setProductPriceEnabled',
        'setProductName',
        'setProductPrice',
        'setHeaderAlignment',
        'setGamepadButtonsColor',
        'setArButtonEnabled',
        'setResetButtonEnabled',
        'setDimensionsButtonEnabled',
        'setScreenshotButtonEnabled',
        'setArLoading',
      ]),

      loadViewer () {
        let source = scheme.source
        let catalog = ''
        let reference = ''
        let sku = ''

        const options = {
          hideButtonFullscreen: true,
          hideButtonDimensions: true,
          hideButtonReset: true,
          hideButtonScreenshot: true,
          hideTuto: true,
          activateDownloadScreenshot: true,
          backgroundShade: { shadeTop: { r: 255, g: 255, b: 255 }, shadeDown: { r: 255, g: 255, b: 255 } },
        }

        const queryParameters = new URLSearchParams(window.location.search)
        const entries = queryParameters.entries()

        for (const entry of entries) {
          const key = entry[0]
          const value = entry[1]

          if (key === 'cid') source = `https://${value}.cloudfront.net/`
          else if (key === 'tid') source = 'https://innersense.fr/configurateurWeb/eatyourhamburgerapollo/' + value + '/'
          else if (key === 'cat') catalog = value
          else if (key === 'ref') reference = value
          else if (key === 'sku') sku = value
          else if (key === 'splashImg' && scheme.isSplashOverridable) options.loaderSrc = value
          else if (key === 'loaderGif' && scheme.isLoaderOverridable) options.loaderGifSrc = value
          else if (key === 'backgroundColor') options.backgroundShade = helpers.buildBackgroundShade(value)
          else if (key === 'buttonsColor' && scheme.isGamepadColorOverridable) this.setGamepadButtonsColor(value)
          else if (key === 'headerAlignment') this.setHeaderAlignment(value)
          else if (key === 'dividers') this.dividers = helpers.deserializeBoolean(value)
          else if (key === 'activateName') this.setProductNameEnabled(helpers.deserializeBoolean(value))
          else if (key === 'activatePrice') this.setProductPriceEnabled(helpers.deserializeBoolean(value))
          else if (key === 'activateAR') this.setArButtonEnabled(helpers.deserializeBoolean(value))
          else if (key === 'activateReset') this.setResetButtonEnabled(helpers.deserializeBoolean(value))
          else if (key === 'activateDimensions') this.setDimensionsButtonEnabled(helpers.deserializeBoolean(value))
          else if (key === 'activateScreenshot') this.setScreenshotButtonEnabled(helpers.deserializeBoolean(value))
          else if (key === 'activateFrontScreenshot') this.isFrontScreenshotEnabled = helpers.deserializeBoolean(value)
          else if (key === 'activateStartupConfiguration') options.activateInitConfiguration = helpers.deserializeBoolean(value)
          else if (key === 'activateTranslation') options.deactivateCameraMovements = !helpers.deserializeBoolean(value)
          else if (key === 'activateRotation') options.deactivateCameraRotation = !helpers.deserializeBoolean(value)
          else if (key === 'activateZoom') options.deactivateCameraZoom = !helpers.deserializeBoolean(value)
          else if (key === 'defaultHorizontalAngle' && isFinite(helpers.deserializeFloat(value))) options.cameraHorizontalAngle = helpers.deserializeFloat(value)
          else if (key === 'defaultVerticalAngle' && isFinite(helpers.deserializeFloat(value))) options.cameraVerticalAngle = helpers.deserializeFloat(value)
          else if (key === 'defaultZoom' && isFinite(helpers.deserializeFloat(value))) options.cameraDistanceFactor = helpers.deserializeFloat(value)
          else if (key === 'nearZoom' && isFinite(helpers.deserializeFloat(value))) options.cameraMinDistanceScale = helpers.deserializeFloat(value)
          else if (key === 'awayZoom' && isFinite(helpers.deserializeFloat(value))) options.cameraMaxDistanceScale = helpers.deserializeFloat(value)
        }

        if (catalog && catalog.length > 0) {
          source = `${source}?cat=${catalog}`
          if (reference && reference.length > 0) source = `${source}&ref=${reference}`
          else if (sku && sku.length > 0) source = `${source}&sku=${sku}`
        } else if (reference && reference.length > 0) {
          source = `${source}?ref=${reference}`
        } else if (sku && sku.length > 0) {
          source = `${source}?sku=${sku}`
        }

        options.type = 'init'
        const viewer = helpers.getViewer()
        viewer.setAttribute('src', source)
        viewer.addEventListener('load', () => {
          viewer.contentWindow.postMessage(options, '*')
        })
      },

      listenMessages (event) {
        if (event.data.origin === 'parent') { this.processParentMessages(event.data) } else { this.processViewerMessages(event.data) }
      },

      processParentMessages (action) {
        switch (action.type) {
          case 'getSummary':
            transmit.getSummary()
            break
          case 'getConfiguration':
            transmit.getConfiguration()
            break
        }
      },

      processViewerMessages (action) {
        switch (action.type) {
          case 'furnitureLoaded':
            this.setProductLoaded(true)
            transmit.getName()
            transmit.getPrice()
            break
          case 'webARFiles' :
            // In static AR mode: Do not display the AR button if statics files doesn't exist
            if (!scheme.isDynamicAR && (!action.fileUSDZ || !action.fileGLB)) this.setArButtonEnabled(false)
            break
          case 'externalAROpening' :
            this.setArLoading(false)
            this.displayQrcodeAr(action.urlQR)
            break
          case 'successAR' :
            this.setArLoading(false)
            break
          case 'APIFullConfig':
            this.options = JSON.parse(action.fullconfigObject)
            transmit.getPrice()
            break
          case 'shadeUpdated':
          case 'accessoryUpdated':
            transmit.getPrice()
            break
          case 'furnitureName':
            this.setProductName(action.name)
            transmit.postName(action.name)
            break
          case 'configurationPrice':
            this.setProductPrice(action.priceAsString)
            transmit.postPrice(action.priceAsString)
            break
          case 'configurationSummary':
            transmit.postSummary(action.configSummary)
            break
          case 'configurationCode':
            transmit.postConfiguration(action.configCode)
            break
          case 'badRequest':
            this.error(action.message)
            break
        }
      },

      resetAction () { transmit.reset() },
      dimensionsAction () { transmit.dimensions() },
      screenshotAction () { this.isFrontScreenshotEnabled ? transmit.screenshotCentered() : transmit.screenshot() },
      loadOptionsItem (item) { transmit.loadOption(item) },

      arAction () {
        this.setArLoading(true)
        scheme.isDynamicAR ? transmit.launchDynamicAR() : transmit.launchStaticicAR()
      },

      displayQrcodeAr (url) {
        this.qrcodeArUrl = url
        this.isQrcodeArDisplayed = true
      },
    },
  }
</script>
